import { PageHeader } from '@/client/components/common/PageHeader';
import { StatCard } from '@/client/components/common/StatCard';
import {
  type InvoiceStatus,
  InvoiceStatusBadge,
} from '@/client/components/invoices/InvoiceStatusBadge';
import { Button } from '@/client/components/ui/button';
import { Card } from '@/client/components/ui/card';
import { Input } from '@/client/components/ui/input';
import { useAdminGuard } from '@/client/hooks/useAdminGuard';
import { type Currency, fmtAmount } from '@/client/lib/currency';
import { trpc } from '@/client/utils/trpc';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FilePen,
  type LucideIcon,
  Plus,
  Receipt,
  Search,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { z } from 'zod';

const searchSchema = z.object({
  status: z.enum(['ALL', 'SENT', 'PAID', 'OVERDUE', 'DRAFT']).optional().default('ALL'),
});

export const Route = createFileRoute('/_app/invoices/')({
  validateSearch: (s: Record<string, unknown>) => searchSchema.parse(s),
  component: InvoicesListPage,
});

function fmtEur(n: number): string {
  return `€${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n)}`;
}

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

type StatusFilter = 'ALL' | 'SENT' | 'PAID' | 'OVERDUE' | 'DRAFT';

const STATUS_TABS: {
  value: StatusFilter;
  label: (kpis: { drafts: number; sentCount: number }) => string;
}[] = [
  { value: 'ALL', label: () => 'All' },
  { value: 'SENT', label: () => 'Outstanding' },
  { value: 'PAID', label: () => 'Paid' },
  { value: 'OVERDUE', label: () => 'Overdue' },
  { value: 'DRAFT', label: ({ drafts }) => (drafts > 0 ? `Drafts · ${drafts}` : 'Drafts') },
];

function KpiCard({
  label,
  value,
  sub,
  valueClass,
  icon,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  valueClass?: string;
  icon?: LucideIcon;
  tone?: 'default' | 'primary' | 'good' | 'warn' | 'bad';
}) {
  return (
    <StatCard
      label={label}
      value={<span className={valueClass}>{value}</span>}
      hint={sub}
      icon={icon}
      tone={tone}
    />
  );
}

function InvoicesListPage() {
  useAdminGuard();

  const search = Route.useSearch();
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const { data, isLoading } = trpc.invoices.list.useQuery();
  const invoices = data?.invoices ?? [];
  const kpis = data?.kpis ?? {
    outstanding: 0,
    paidThisMonth: 0,
    overdue: 0,
    drafts: 0,
    sentCount: 0,
  };

  const filtered = useMemo(() => {
    let result = invoices;
    if (search.status !== 'ALL') result = result.filter((i) => i.status === search.status);
    if (q.trim()) {
      const lower = q.toLowerCase();
      result = result.filter(
        (i) =>
          i.number.toLowerCase().includes(lower) ||
          i.client_name.toLowerCase().includes(lower) ||
          (i.project_name ?? '').toLowerCase().includes(lower),
      );
    }
    return result;
  }, [invoices, search.status, q]);

  function setStatus(s: StatusFilter) {
    navigate({ to: '/invoices', search: { status: s } });
  }

  const now = new Date();
  const monthName = now.toLocaleString('en-US', { month: 'long' });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Invoices"
        eyebrow="Billing"
        icon={Receipt}
        actions={
          <Button size="sm" asChild>
            <Link to="/invoices/new">
              <Plus className="h-4 w-4" />
              New invoice
            </Link>
          </Button>
        }
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          label="Outstanding"
          value={fmtEur(kpis.outstanding)}
          sub={`${kpis.sentCount} invoice${kpis.sentCount !== 1 ? 's' : ''}`}
          icon={Clock}
          tone="primary"
        />
        <KpiCard
          label={`Paid · ${monthName}`}
          value={fmtEur(kpis.paidThisMonth)}
          valueClass="text-good"
          icon={CheckCircle2}
          tone="good"
        />
        <KpiCard
          label="Overdue"
          value={fmtEur(kpis.overdue)}
          valueClass={kpis.overdue > 0 ? 'text-destructive' : undefined}
          icon={AlertTriangle}
          tone={kpis.overdue > 0 ? 'bad' : 'default'}
        />
        <KpiCard
          label="Drafts"
          value={String(kpis.drafts)}
          sub={kpis.drafts > 0 ? 'ready to send' : 'none pending'}
          icon={FilePen}
          tone="warn"
        />
      </div>

      <Card className="overflow-hidden">
        {/* Tab strip + search */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-2 sm:px-5">
          <div className="-mx-1 flex gap-1 overflow-x-auto px-1">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setStatus(tab.value)}
                className={[
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  search.status === tab.value
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground',
                ].join(' ')}
              >
                {tab.label(kpis)}
              </button>
            ))}
          </div>
          <div className="hidden flex-1 sm:block" />
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Find an invoice…"
              className="pl-9 h-8 text-sm"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">No invoices found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] whitespace-nowrap text-sm">
              <thead>
                <tr className="border-b text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <th className="px-5 py-3 text-left">Invoice</th>
                  <th className="px-5 py-3 text-left">Client</th>
                  <th className="px-5 py-3 text-left">Project</th>
                  <th className="px-5 py-3 text-left">Issued</th>
                  <th className="px-5 py-3 text-left">Due</th>
                  <th className="px-5 py-3 text-left">Status</th>
                  <th className="px-5 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-b last:border-0 hover:bg-muted/40 transition-colors cursor-pointer"
                    onClick={() => navigate({ to: '/invoices/$id', params: { id: inv.id } })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate({ to: '/invoices/$id', params: { id: inv.id } });
                      }
                    }}
                    tabIndex={0}
                  >
                    <td className="px-5 py-3 font-mono font-semibold">{inv.number}</td>
                    <td className="px-5 py-3 font-medium">{inv.client_name}</td>
                    <td className="px-5 py-3 text-muted-foreground">{inv.project_name ?? '—'}</td>
                    <td className="px-5 py-3 tabular-nums text-muted-foreground">
                      {fmtDate(inv.issue_date)}
                    </td>
                    <td className="px-5 py-3 tabular-nums text-muted-foreground">
                      {fmtDate(inv.due_date)}
                    </td>
                    <td className="px-5 py-3">
                      <InvoiceStatusBadge status={inv.status as InvoiceStatus} />
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums font-semibold">
                      {fmtAmount(inv.total, inv.currency as Currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
